import { useEffect, useId, useState } from 'react';

interface ImageUploadFieldProps {
  label?: string;
  existingImageName?: string | null;
  existingImageSrc?: string | null;
  onImageNameChange: (imageName: string | null) => void;
  onFileChange: (file: File | null) => void;
}

interface SelectedImage {
  file: File | null;
  preview: string | null;
}

export default function ImageUploadField({
  label = 'Vehicle Image',
  existingImageName,
  existingImageSrc,
  onImageNameChange,
  onFileChange,
}: ImageUploadFieldProps) {
  const inputId = useId();
  const [selectedImage, setSelectedImage] = useState<SelectedImage>({
    file: null,
    preview: null,
  });

  useEffect(() => {
    return () => {
      if (selectedImage.preview) {
        URL.revokeObjectURL(selectedImage.preview);
      }
    };
  }, [selectedImage.preview]);

  const displayImageSrc = selectedImage.preview ?? existingImageSrc ?? null;

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      return;
    }

    if (selectedImage.preview) {
      URL.revokeObjectURL(selectedImage.preview);
    }

    const preview = URL.createObjectURL(file);
    setSelectedImage({ file, preview });
    onFileChange(file);
    onImageNameChange(file.name.replace(/[^a-zA-Z0-9._-]/g, '_'));
  };

  const handleRemoveImage = () => {
    if (selectedImage.preview) {
      URL.revokeObjectURL(selectedImage.preview);
    }
    setSelectedImage({ file: null, preview: null });
    onFileChange(null);
    onImageNameChange(null);
  };

  return (
    <div className="col-span-full">
      <h3 className="mb-4 text-base font-semibold text-gray-900 dark:text-white/90">
        {label}
      </h3>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <div className="flex-1 space-y-3">
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            onChange={handleImageChange}
            className="hidden"
            id={inputId}
          />
          <div className="flex flex-wrap items-center gap-3">
            <label
              htmlFor={inputId}
              className="inline-flex cursor-pointer items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              {displayImageSrc ? 'Change Image' : 'Upload Image'}
            </label>
            {displayImageSrc && (
              <button
                type="button"
                onClick={handleRemoveImage}
                className="inline-flex items-center rounded-lg border border-error-200 bg-error-50 px-4 py-2 text-sm font-medium text-error-700 transition-colors hover:bg-error-100 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400"
              >
                Remove
              </button>
            )}
          </div>
          {selectedImage.file && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Selected: {selectedImage.file.name}
            </p>
          )}
          {existingImageName && !selectedImage.file && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Current file: {existingImageName}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400">
            PNG, JPG, WebP, or GIF. Max recommended size 5 MB.
          </p>
        </div>

        <div className="shrink-0">
          {displayImageSrc ? (
            <div className="h-36 w-36 overflow-hidden rounded-xl border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900">
              <img
                src={displayImageSrc}
                alt="Vehicle preview"
                className="h-full w-full object-cover"
                onError={(e) => {
                  if (!e.currentTarget.src.includes('placeholder-image.png')) {
                    e.currentTarget.src = '/images/placeholder-image.png';
                  }
                }}
              />
            </div>
          ) : (
            <div className="flex h-36 w-36 items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-400 dark:border-gray-700 dark:bg-gray-900">
              No image
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
